import { useState } from 'react';

import {
  ExclamationTriangleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';

import accountsAPI from '@/api/accounts';
import Button, { BUTTON_KIND } from '@/components/Button';
import Modal from '@/components/Modals/Modal';
import useAuth from '@/hooks/useAuth';

type PlanRequiredModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  checkoutUrl: string;
};

export function PlanRequiredModal({
  isOpen,
  setIsOpen,
  checkoutUrl,
}: PlanRequiredModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      successButton={
        <Button
          icon={<SparklesIcon />}
          kind={BUTTON_KIND.PRIMARY}
          buttonText="Upgrade Now"
          external
          externalBlankTarget
          href={checkoutUrl}
        />
      }
      cancelButton={
        <Button
          kind={BUTTON_KIND.WHITE}
          buttonText="Support"
          external
          externalBlankTarget
          href={'https://help.tapt.io'}
        />
      }
      onClose={() => setIsOpen(false)}
      onCancel={() => setIsOpen(false)}
      onSuccess={() => setIsOpen(false)}
    >
      <div className="space-y-3">
        <div className="text-2xl not-italic leading-8 text-red-500 flex items-center space-x-2 max-md:max-w-full">
          <ExclamationTriangleIcon className="h-5 w-5" />
          &nbsp;
          <span className="font-medium">Subscription Required</span>
        </div>
        <div className="text-sm not-italic leading-5 text-gray-600 max-md:max-w-full">
          You don&apos;t have an active platform access plan on your account. To
          access the dashboard, start a plan by clicking Upgrade Now below. If
          this is an error, please contact support.
        </div>
      </div>
    </Modal>
  );
}

type ModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export function CancellationConfirmationModal({
  isOpen,
  setIsOpen,
}: ModalProps) {
  const { orgID } = useAuth();

  const [isChecked, setIsChecked] = useState(false);

  const { mutate: cancelSubscription, isPending: isCancellingSubscription } =
    useMutation({
      mutationKey: ['sendSubscriptionCancellationEmail', orgID],
      mutationFn: accountsAPI.sendSubscriptionCancellationEmail,
    });

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      dialogTitle="Are you sure you want to cancel your platform fee?"
      dialogDescription={
        <>
          <p>
            We will be sad to see you go, but you can always renew your platform
            fee!
          </p>
          <br />
          <p>
            Your request to cancel will be sent. An account manager may be in
            contact with you to verify your cancellation. Otherwise, you will
            receive an email notification once your request has been processed.
          </p>
        </>
      }
      checkbox={isChecked}
      setCheckbox={setIsChecked}
      checkboxDescription="I am sure I want to cancel my platform fee"
      successButtonText="Proceed"
      onSuccess={() => {
        if (orgID) {
          cancelSubscription(orgID);
        }

        setIsOpen(false);
      }}
      isDisabled={!isChecked}
      isLoading={isCancellingSubscription}
    />
  );
}
